<template>
  <div class="intent-stat">
    <!-- 头部筛选与维度切换 -->
    <div class="intent-stat-header">
      <div class="intent-stat-tabs">
        <div
          v-for="tab in intentStatTabs"
          :key="tab.key"
          class="intent-stat-tab"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </div>
      </div>
      <div class="intent-stat-actions">
        <a-select size="small" :value="filterValue" class="filter-select">
          <a-select-option value="filter">筛选</a-select-option>
          <a-select-option value="month">2024-08</a-select-option>
        </a-select>
      </div>
    </div>

    <!-- 折线图：意向趋势 -->
    <div class="intent-section">
      <div class="intent-section-title">楼栋意向统计</div>
      <div ref="intentLine" class="intent-line-chart"></div>
    </div>

    <!-- 饼图 + 明细：意向分析 -->
    <div class="intent-section intent-analysis">
      <div class="intent-section-title">
        1栋 意向分析
        <span class="intent-total">房源：{{ analysisTotal }}</span>
      </div>
      <div class="intent-analysis-body">
        <div ref="intentPie" class="intent-pie-chart"></div>
        <div class="intent-analysis-list">
          <div class="intent-analysis-header">
            <span>意向</span>
            <span>客户(组)</span>
            <span>意向比</span>
          </div>
          <div class="intent-analysis-row" v-for="item in analysisList" :key="item.label">
            <span class="analysis-label">
              <span class="analysis-dot" :style="{ background: item.color }"></span>
              {{ item.label }}
            </span>
            <span>{{ item.count }}</span>
            <span>{{ item.percent }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'IntentStat',

  props: {
    xAxis: { type: Array, default: () => [] },
    series: { type: Array, default: () => [] },
    analysisTotal: { type: String, default: '' },
    analysisList: { type: Array, default: () => [] }
  },

  data() {
    return {
      activeTab: 'building',
      filterValue: 'filter',
      intentLineChart: null,
      intentPieChart: null,
      resizeHandler: null,
      intentStatTabs: [
        { key: 'building', label: '楼栋意向' },
        { key: 'unit', label: '单元意向' },
        { key: 'floor', label: '楼层意向' },
        { key: 'room', label: '房源意向' },
        { key: 'layout', label: '户型意向' }
      ]
    }
  },

  watch: {
    // 数据变化时重绘折线图
    xAxis: {
      handler() {
        this.renderIntentLine()
      },
      deep: true
    },
    series: {
      handler() {
        this.renderIntentLine()
      },
      deep: true
    },
    // 饼图数据变化时同步刷新
    analysisList: {
      handler() {
        this.renderIntentPie()
      },
      deep: true
    }
  },

  mounted() {
    this.renderIntentLine()
    this.renderIntentPie()
    this.resizeHandler = () => {
      if (this.intentLineChart) {
        this.intentLineChart.resize()
      }
      if (this.intentPieChart) {
        this.intentPieChart.resize()
      }
    }
    window.addEventListener('resize', this.resizeHandler)
  },

  beforeDestroy() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    if (this.intentLineChart) {
      this.intentLineChart.dispose()
      this.intentLineChart = null
    }
    if (this.intentPieChart) {
      this.intentPieChart.dispose()
      this.intentPieChart = null
    }
  },

  methods: {
    // 折线图：根据楼栋维度展示意向趋势
    renderIntentLine() {
      if (!this.$refs.intentLine) {
        return
      }
      if (this.intentLineChart) {
        this.intentLineChart.dispose()
      }
      this.intentLineChart = echarts.init(this.$refs.intentLine)
      this.intentLineChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: {
          top: 0,
          data: this.series.map(item => item.name)
        },
        grid: { left: 30, right: 20, top: 30, bottom: 30 },
        xAxis: {
          type: 'category',
          data: this.xAxis
        },
        yAxis: { type: 'value' },
        series: this.series.map(item => ({
          name: item.name,
          type: 'line',
          data: item.data,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { color: item.color },
          itemStyle: { color: item.color }
        }))
      })
    },
    // 饼图：展示意向占比
    renderIntentPie() {
      if (!this.$refs.intentPie) {
        return
      }
      if (this.intentPieChart) {
        this.intentPieChart.dispose()
      }
      this.intentPieChart = echarts.init(this.$refs.intentPie)
      this.intentPieChart.setOption({
        tooltip: { trigger: 'item' },
        legend: { show: false },
        series: [
          {
            type: 'pie',
            radius: ['45%', '70%'],
            center: ['50%', '50%'],
            label: { show: false },
            data: this.analysisList.map(item => ({
              name: item.label,
              value: item.count,
              itemStyle: { color: item.color }
            }))
          }
        ]
      })
    }
  }
}
</script>

<style lang="less" scoped>
.intent-stat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.intent-stat-tabs {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #5b6b7c;
}

.intent-stat-tab {
  padding: 4px 0;
  cursor: pointer;
  position: relative;
}

.intent-stat-tab.active {
  color: #1f2f3d;
  font-weight: 600;
}

.intent-stat-tab.active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -6px;
  height: 3px;
  border-radius: 2px;
  background: #2b6de5;
}

.intent-stat-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.intent-section {
  background: #ffffff;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid #f0f0f0;
}

.intent-section-title {
  font-weight: 600;
  color: #1f2f3d;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.intent-total {
  font-size: 12px;
  color: #5b6b7c;
}

.intent-line-chart {
  height: 220px;
}

.intent-analysis-body {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  align-items: center;
}

.intent-pie-chart {
  height: 120px;
}

.intent-analysis-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 12px;
  color: #5b6b7c;
}

.intent-analysis-header,
.intent-analysis-row {
  display: grid;
  grid-template-columns: 1fr 70px 70px;
  gap: 6px;
  align-items: center;
}

.intent-analysis-header {
  color: #1f2f3d;
  font-weight: 600;
}

.analysis-label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.analysis-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
</style>
